/**
 * @fileoverview Resend-activation-code mutation hook.
 * @description Wraps POST /identity/activation/mfa/resend in a TanStack mutation and, on success,
 * replaces the stored challenge with the rotated one (new id, fresh expiry and resend cooldown,
 * converted from the response's relative seconds), so the form's countdowns restart.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'

import {
  resendActivationCode,
  type ResendActivationCodeRequest,
  type ResendActivationCodeResponse,
} from '@/features/auth/services/activation.service'
import { toActivationChallenge } from '@/shared/lib/auth/two-factor-challenge.utils'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Requests a fresh activation code and, on success, replaces the stored challenge.
 * @returns {ReturnType<typeof useMutation<ResendActivationCodeResponse, ApiError,
 * ResendActivationCodeRequest>>} The mutation object (mutate, isPending, error, ...).
 */
export function useResendActivationCode() {
  const { challenge, setChallenge } = useTwoFactorChallenge()

  return useMutation<ResendActivationCodeResponse, ApiError, ResendActivationCodeRequest>({
    mutationFn: resendActivationCode,
    onSuccess: (response) => {
      setChallenge(toActivationChallenge(response, challenge?.email ?? ''))
    },
  })
}
